import { computed, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { PaymentDocument, SettlementLink } from '../models/settlement-action.model';
import { Settlement } from '../models/settlement.model';
import { SettlementActionRepository } from '../services/settlement-action.repository';
import { SettlementQueryRepository } from '../services/settlement-query.repository';
import { rejectionMessage } from '../utils/action-rejection.util';

export type PaymentDocumentsStatus = 'loading' | 'ready' | 'empty' | 'error';
export type SettlementLinkStatus = 'idle' | 'submitting' | 'linked' | 'rejected' | 'error';

const ERROR_SUBJECT = 'la vinculación del DTE';
const REJECTION_FALLBACK = 'No se pudo vincular el DTE seleccionado.';

@Injectable()
export class SettlementLinkStore {
  private readonly actions = inject(SettlementActionRepository);
  private readonly query = inject(SettlementQueryRepository);

  private readonly _settlement = signal<Settlement | null>(null);
  private readonly _documents = signal<readonly PaymentDocument[]>([]);
  private readonly _documentsStatus = signal<PaymentDocumentsStatus>('loading');
  private readonly _selectedDteId = signal<number | null>(null);
  private readonly _status = signal<SettlementLinkStatus>('idle');
  private readonly _link = signal<SettlementLink | null>(null);
  private readonly _rejection = signal<string | null>(null);
  private readonly _error = signal<AppError | null>(null);

  private settlementId: number | null = null;
  private requests = new Subscription();

  readonly settlement = this._settlement.asReadonly();
  readonly documents = this._documents.asReadonly();
  readonly documentsStatus = this._documentsStatus.asReadonly();
  readonly selectedDteId = this._selectedDteId.asReadonly();
  readonly status = this._status.asReadonly();
  readonly link = this._link.asReadonly();
  readonly rejection = this._rejection.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isSubmitting = computed(() => this._status() === 'submitting');

  readonly selectedTotal = computed(() => {
    const id = this._selectedDteId();
    return this._documents().find((document) => document.dteId === id)?.totalAmount ?? 0;
  });

  // El store vive en la ruta: sin esto, al volver a la página se repetiría el último resultado.
  reset(): void {
    this.requests.unsubscribe();
    this.requests = new Subscription();
    this.settlementId = null;
    this._settlement.set(null);
    this._documents.set([]);
    this._selectedDteId.set(null);
    this._link.set(null);
    this._rejection.set(null);
    this._error.set(null);
    this._status.set('idle');
  }

  open(settlementId: number): void {
    this.reset();
    this.settlementId = settlementId;

    this.requests.add(
      this.query.getById(settlementId).subscribe({
        next: (settlement) => this._settlement.set(settlement),
        error: (cause: unknown) => this._error.set(toAppError(cause, ERROR_SUBJECT)),
      }),
    );
    this.loadDocuments();
  }

  select(dteId: number): void {
    this._selectedDteId.set(dteId);
  }

  submit(): void {
    const dteId = this._selectedDteId();
    if (this.settlementId === null || dteId === null || this.isSubmitting()) return;

    this._rejection.set(null);
    this._error.set(null);
    this._status.set('submitting');

    this.actions.link(this.settlementId, dteId).subscribe({
      next: (link) => {
        this._link.set(link);
        this._status.set('linked');
      },
      error: (cause: unknown) => {
        const rejection = rejectionMessage(cause, REJECTION_FALLBACK);
        if (rejection) {
          this._rejection.set(rejection);
          this._status.set('rejected');
          this.loadDocuments();
          return;
        }
        this._error.set(toAppError(cause, ERROR_SUBJECT));
        this._status.set('error');
      },
    });
  }

  private loadDocuments(): void {
    if (this.settlementId === null) return;
    this._selectedDteId.set(null);
    this._documentsStatus.set('loading');

    this.requests.add(
      this.actions.listPaymentDocuments(this.settlementId).subscribe({
        next: (documents) => {
          this._documents.set(documents);
          this._documentsStatus.set(documents.length ? 'ready' : 'empty');
        },
        error: (cause: unknown) => {
          this._documents.set([]);
          this._documentsStatus.set('error');
          this._rejection.set(rejectionMessage(cause, REJECTION_FALLBACK));
          if (!this._rejection()) this._error.set(toAppError(cause, ERROR_SUBJECT));
        },
      }),
    );
  }
}
